import { Cloudinary } from "@cloudinary/url-gen"
import { Resize } from "@cloudinary/url-gen/actions/resize"
import { useMemo } from "react"

import {
  Sliders,
  SliderType,
  TeaserDimension,
} from "../components/Sliders/Slider"
import {
  MyStagePageQuery,
  PublicationLevel,
  SliderLayout,
} from "../generated/graphql"
import { ArrayElement } from "../types"
import { ccFactor, CloudinaryConf, secondsToMinutes } from "../utilities/common"
import { s } from "../utilities/common"

import { MystageSliderId } from "./use-browse-screen"
import useTranslate from "./use-translate"

const cld = new Cloudinary(CloudinaryConf)

export type MystageSlider = Extract<
  MyStagePageQuery["currentUser"],
  { __typename: "User" }
>[
  | "videoRecentlyPlayed"
  | "videoFavorites"
  | "albumRecentlyPlayed"
  | "albumFavorites"
  | "editorialPlaylistFavorites"]
export const useMystageSliders = (
  id: MystageSliderId,
  title: string,
  slider: MystageSlider | undefined,
) => {
  const t = useTranslate()

  const getSlider = (
    id: MystageSliderId,
    title: string,
    slider: MystageSlider | undefined,
  ): ArrayElement<Sliders> | undefined => {
    return !(slider && slider.edges[0])
      ? undefined
      : {
          layout: SliderLayout.Regular,
          liveTitles: undefined,
          totalResults: slider.totalResults,
          id,
          title,
          type: SliderType.Vertical,
          style:
            slider.edges[0].node.content.__typename === "Track"
              ? TeaserDimension.SquareSmallTeaser
              : slider.edges[0].node.content.__typename === "Album" ||
                  slider.edges[0].node.content.__typename ===
                    "EditorialPlaylist"
                ? TeaserDimension.SquareSmallTeaser
                : TeaserDimension.LandscapeSmallTeaser,
          pageInfo: { hasNextPage: slider.pageInfo.hasNextPage },
          teasers: slider.edges
            // remove dupes
            .filter(
              (value, index, self) =>
                index ===
                self.findIndex(
                  (t) =>
                    (value.node.content.__typename === "Track" &&
                      t.node.content.__typename === "Track" &&
                      "album" in t.node.content &&
                      "album" in value.node.content &&
                      t.node.content.album.id ===
                        value.node.content.album.id) ||
                    t.node.id === value.node.id,
                ),
            )
            // TODO ask if content availability check is relevant
            .map((edge) => {
              const pictureNode =
                edge.node.content.__typename === "PerformanceWork" &&
                "vodConcert" in edge.node.content
                  ? edge.node.content.vodConcert
                  : edge.node.content.__typename === "Video" ||
                      edge.node.content.__typename === "Album" ||
                      edge.node.content.__typename === "EditorialPlaylist"
                    ? edge.node.content
                    : "album" in edge.node.content
                      ? edge.node.content.album
                      : undefined
              const pictures =
                pictureNode && "pictures" in pictureNode
                  ? pictureNode.pictures
                  : undefined
              return {
                id:
                  edge.node.content.__typename === "PerformanceWork" &&
                  "vodConcert" in edge.node.content
                    ? edge.node.content.vodConcert.id
                    : edge.node.content.__typename === "Track" &&
                        "album" in edge.node.content
                      ? edge.node.content.album.id
                      : "id" in edge.node.content
                        ? edge.node.content.id
                        : "",
                uniqueId: "id" in edge.node.content ? edge.node.content.id : "",
                type:
                  edge.node.content.__typename === "PerformanceWork"
                    ? "VodConcert"
                    : edge.node.content.__typename === "Video"
                      ? "Video"
                      : edge.node.content.__typename === "Track" ||
                          edge.node.content.__typename === "Album"
                        ? "Album"
                        : edge.node.content.__typename === "EditorialPlaylist"
                          ? "EditorialPlaylist"
                          : undefined,
                title:
                  edge.node.content.__typename === "PerformanceWork" &&
                  "work" in edge.node.content
                    ? edge.node.content.work.title
                    : (edge.node.content.__typename === "Video" ||
                          edge.node.content.__typename === "Album" ||
                          edge.node.content.__typename ===
                            "EditorialPlaylist") &&
                        "title" in edge.node.content
                      ? edge.node.content.title
                      : edge.node.content.__typename === "Track" &&
                          "title" in edge.node.content
                        ? edge.node.content.album.title
                        : "",
                subtitle:
                  edge.node.content.__typename === "PerformanceWork" &&
                  "vodConcert" in edge.node.content
                    ? "FROM: " + edge.node.content.vodConcert.title
                    : edge.node.content.__typename === "Video" &&
                        "subtitle" in edge.node.content
                      ? edge.node.content.subtitle
                      : edge.node.content.__typename === "Track" &&
                          "album" in edge.node.content
                        ? edge.node.content.album.artistAndGroupDisplayInfo
                        : edge.node.content.__typename === "Album" &&
                            "artistAndGroupDisplayInfo" in edge.node.content
                          ? edge.node.content.artistAndGroupDisplayInfo
                          : undefined,
                caption:
                  (edge.node.content.__typename === "PerformanceWork" ||
                    edge.node.content.__typename === "Video") &&
                  "duration" in edge.node.content
                    ? t("duration__in_minutes", [
                        secondsToMinutes(edge.node.content.duration),
                      ])
                    : undefined,
                label:
                  edge.node.content.__typename === "PerformanceWork" &&
                  "vodConcert" in edge.node.content
                    ? edge.node.content.vodConcert.typeDisplayName
                    : (edge.node.content.__typename === "Video" ||
                          edge.node.content.__typename ===
                            "EditorialPlaylist") &&
                        "typeDisplayName" in edge.node.content
                      ? edge.node.content.typeDisplayName
                      : undefined,
                publicationLevel:
                  edge.node.content.__typename === "PerformanceWork" &&
                  "vodConcert" in edge.node.content
                    ? edge.node.content.vodConcert.publicationLevel
                    : edge.node.content.__typename === "Video" &&
                        "publicationLevel" in edge.node.content
                      ? edge.node.content.publicationLevel
                      : PublicationLevel.TicketRequired,
                /*: edge.node.content.__typename === "Track" // FIXME find our where to get this value
                      ? edge.node.content.publicationLevel*/

                liveConcertType: undefined,
                picture:
                  edge.node.content.__typename === "PerformanceWork" ||
                  edge.node.content.__typename === "Video" ||
                  edge.node.content.__typename === "Track" ||
                  edge.node.content.__typename === "Album" ||
                  edge.node.content.__typename === "EditorialPlaylist"
                    ? cld
                        .image(
                          pictures?.filter(
                            (i) =>
                              i.type ===
                              (edge.node.content.__typename === "Track" ||
                              edge.node.content.__typename === "Album" ||
                              edge.node.content.__typename ===
                                "EditorialPlaylist"
                                ? "COVER"
                                : "TEASER"),
                          )[0]?.url,
                        )
                        .resize(Resize.scale().width(s(ccFactor(587), true)))
                        .format("jpg")
                        .setDeliveryType("fetch")
                        .toURL()
                    : undefined,
                progress:
                  edge.node.content.__typename === "PerformanceWork" &&
                  "mark" in edge.node &&
                  "duration" in edge.node.content
                    ? edge.node.mark / edge.node.content.duration
                    : undefined,
              }
            }),
        }
  }

  return useMemo(() => getSlider(id, title, slider), [id, title, slider])
}
